import * as THREE from 'three';
import { Pool, stdMat } from '../render/scene';
import { collectMeshes } from '../render/enemy-instances';
import { ENEMY_TYPES } from '../data/enemies';
import { BOSS_TYPES } from '../data/boss-stats';
import { buildBossRig } from './boss-rig';
/* ============================================================
   敵人與 Boss
   ============================================================

   ## 這個檔現在有兩種敵人（2026-08-03 的敵人大改版）

   | | 小怪 | Boss |
   |---|---|---|
   | 定義在 | `data/enemies.ts`（造型來自 `entities/families/*`） | 這個檔 |
   | 造型 | part 表（沒有 Object3D 階層） | `build()` 建 Group |
   | 渲染 | `render/enemy-field.ts` 每 part 一顆 InstancedMesh | 真 rig，加進場景 |

   小怪那一路**仍然有一顆 `root`**（空的 `Object3D`，不進場景）。
   保留它不是為了畫圖，是因為既有的模擬層有二十幾處在讀寫
   `e.root.position / rotation.z / scale`（踉蹌傾倒、出場放大、每幀內插）——
   拿掉它要改的呼叫端遠多於留著它的成本，而一顆沒有子節點的 Object3D
   不會被 traverse、不會進 render list，代價只有一個物件的記憶體。

   舊的七隻雜魚（drone/walker/charger/turret/bomber/spitter/splitter）
   在這一版被整批換掉。要撈回來的話在 git 歷史裡，不要重寫。

   ## B2（2026-08-03）：Boss 也整批換掉了

   舊的四隻（core / gemini / monolith / final）與 `boss-kit.ts` / `boss-core.ts`
   一起退場，換成從原型移植的五隻（`data/boss-stats.ts` + `entities/bosses/*`）。
   它們的造型是**真的階層 rig**，動畫是每隻自己的 `upd(rig, o, dt)` ——
   不再是「四隻共用一份通用動畫 + `userData.legs/spin` 幾個固定掛鉤」。
   舊的四隻在 git 歷史裡（`vs-pivot` 分支，B2 之前）。 */
export var ETYPE: Record<string, any> = {};

/* Boss 併進同一張表。**造型與數值在別的檔**（`data/boss-stats.ts`）——
   這裡只做合併，因為 `ETYPE[type]` 是全專案唯一的查表入口。 */
for (var _bk in BOSS_TYPES) ETYPE[_bk] = BOSS_TYPES[_bk];

/* 小怪併進同一張表。併而不是分兩張：`ETYPE[type]` 是全專案的查表入口，
   分成兩張的話每個使用處都要記得查兩次，遲早會漏。 */
for (var _k in ENEMY_TYPES) ETYPE[_k] = ENEMY_TYPES[_k];

/** 小怪：沒有 Object3D 造型，只有一顆空 root 與一個動畫通道物件。

    `o` 是規格 §6.2 的動畫通道 —— **行為程式與造型程式之間唯一的介面**。
    每隻各持有一份並重複使用：420 隻 × 每幀一個新物件會讓 GC 在密度尖峰時
    變成可觀的成本，而這個物件的欄位是固定的。 */
function makeSwarm(typeName, T) {
  var root = new THREE.Object3D();
  return {
    type: typeName, T: T, root: root, body: null, mats: null, parts: null,
    hp: T.hp, hpMax: T.hp, r: T.r, pos: new THREE.Vector3(), vel: new THREE.Vector3(),
    flash: 0, dirty: false, phase: 0, st: 0, cd: 0, ph: 0, face: 0, side: 1, dmgMul: 1,
    /* 動畫時鐘的個體偏移（`spawn.ts` 從 `phase` 推導）。全場共用同一個 `t`
       的話，同型的三百隻會同步呼吸與自轉。 */
    tOff: 0,
    /* 行為狀態機（`sim/enemy-ai.ts`）：0 巡航 / 1 預備 / 2 攻擊 / 3 恢復。
       `tel`/`atk` 是 0→1 的進度，直接餵給造型的 `fn(P, o)`。 */
    tel: 0, atk: 0, mv: 0, vi: 0, q: {},
    /* 支援類加成（`mark` 增傷增速 / `guard` 減傷），到期一起還原。 */
    buffT: 0, buffDmg: 1, buffSpd: 1, buffArm: 1,
    /* 載荷用的暫存（`sim/enemy-atk.ts`）：鎖定的落點、旋掃的判定加成、
       牽引的剩餘時間。**池化物件，`spawnEnemy` 一定要清**。 */
    tgx: 0, tgz: 0, spinR: 0, pullT: 0,
    o: {
      x: 0, z: 0, yaw: 0, t: 0, ph: 0, mv: 0, tel: 0, atk: 0, hit: 0, die: 0,
      p2: 0, sc: 1, q: {}, ry: 0, rp: 0, rr: 0, rz: 0
    }
  };
}

/** BOSS：真的階層 rig（`entities/bosses/*` 的 `build()`），動畫是自己的
    `upd(rig, o, dt)`。動畫通道 `o` 與小怪同一份規格（§6.2），
    只是多了 Boss 才有的 `stg`/`p2`/`broken`/`aim`。 */
function makeBoss(typeName, T) {
  var b = buildBossRig(T);
  return {
    type: typeName, T: T, root: b.root, body: b.body, rig: b.rig,
    mats: b.mats, marks: b.marks, parts: collectMeshes(b.root),
    hp: T.hp, hpMax: T.hp, r: T.r, pos: new THREE.Vector3(), vel: new THREE.Vector3(),
    flash: 0, dirty: false, phase: 0, st: 0, cd: 0, ph: 0, face: 0, side: 1, dmgMul: 1,
    tOff: 0, tel: 0, atk: 0, mv: 0,
    /* Boss 專屬。`stg` 是階段序號（0 起），`invulnT` 是轉場與召喚護盾的
       無敵剩餘時間，`partHp` 是各弱點的剩餘結構值（`sim/boss-parts.ts`）。 */
    stg: 0, invulnT: 0, shield: 0, shieldT: 0, adds: 0,
    partHp: null, broken: 0, legDead: 0, podDead: 0
  };
}

export function makeEnemy(typeName) {
  var T = ETYPE[typeName];
  if (T.fam) return makeSwarm(typeName, T);
  if (T.bossRig) return makeBoss(typeName, T);
  var m1 = stdMat(T.body, { metal: 0.45, rough: 0.55, flat: true });
  var m2 = stdMat(0x0a0d14, { em: T.accent, ei: 1.6, flat: true });
  var root = new THREE.Group(), body = new THREE.Group();
  body.position.y = T.y;
  /* builder 可以**回傳自己的材質清單**（Boss 走這條：每隻一份色盤）。
     沒回傳就沿用 `m1`/`m2` —— 雜魚與精英維持原樣。
     `mats` 之後被受擊閃白與衝擊發光讀取，而 `sim/elites.ts` 只改 `mats[1]`，
     所以任何色盤的第二個都必須是**發光**那一個。 */
  var own = T.build(body, m1, m2);
  root.add(body);
  /* **只有 Boss 投影**（使用者 2026-08-02 指定）。理由是它要「精緻」——
     Boss 是全場唯一會被玩家盯著看的敵人，真實投影讓它有重量。
     投影的物件不加 blob 假影子（兩個疊在一起會看到多餘的橢圓）。

     小怪走 `enemy-field.ts` 的共用壓扁圓盤，一個 draw call 罩全場。 */
  root.traverse(function (o: any) { if (o.isMesh) o.castShadow = true; });
  return {
    /* `parts` 是實例化渲染要複製的子 mesh 清單，**在這裡就快取起來**：
       精英的標記（金環 + 光柱）是之後才掛到 root 上的，晚一步收集的話
       清單長度會跟同型別的其他隻對不上，實例化就整個錯位。 */
    type: typeName, T: T, root: root, body: body, mats: own || [m1, m2], parts: collectMeshes(root),
    hp: T.hp, hpMax: T.hp, r: T.r, pos: new THREE.Vector3(), vel: new THREE.Vector3(),
    flash: 0, dirty: false, phase: 0, st: 0, cd: 0, ph: 0, face: 0, side: 1, dmgMul: 1
  };
}

/* 池子容量。**池子比同屏上限小的話，上限是假的**：`Pool.get()` 回 null，
   編隊靜默少一半，而且是「有時候少」（看當下的敵種分布），最難查的那種。
   小怪各池的容量寫在 `data/enemies.ts` 的 `cap`（合計 850 > `ENEMY_CAP` 420），
   跟數值放在一起，因為它跟生成權重是同一件事的兩半。 */
export var POOLS: Record<string, any> = {};
/* Boss 一隻一個池、容量 1（一場只出現一次，而且同時只會有一隻）。
   ⚠️ 池子在**開機時**就建好，等於開機時建五具完整的 rig（KAISER 97 顆 mesh）。
   延遲建構會把成本移到 Boss 降臨的那一幀 —— 那正是最不能掉幀的一刻。 */
for (var _bp in BOSS_TYPES) {
  POOLS[_bp] = new Pool(1, (function (id) {
    return function () { return makeEnemy(id); };
  })(_bp));
}
for (var _p in ENEMY_TYPES) {
  POOLS[_p] = new Pool(ENEMY_TYPES[_p].cap, (function (id) {
    return function () { return makeEnemy(id); };
  })(_p));
}

export var enemies = [], boss = null;
/* boss 由別的模組指派，但 ES 模組的 import 是唯讀繫結，所以走 setter。 */
export function setBoss(b) { boss = b; }
